/** Angular imports */
import { DatePipe, isPlatformBrowser } from '@angular/common';
import {
    AfterViewInit,
    Component,
    computed,
    DestroyRef,
    effect,
    ElementRef,
    inject,
    NgZone,
    OnDestroy,
    OnInit,
    PLATFORM_ID,
    signal,
    TemplateRef,
    untracked,
    viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { EnvironmentService } from '@core';
import { catchError, EMPTY, forkJoin, Subscription } from 'rxjs';

/** Component imports */
import { FormsModule } from '@angular/forms';
import {
    Button,
    DropdownOption,
    DuplicateResult,
    Input,
    Modal,
    ThumbnailItem,
    ThumbnailItemComponent,
    ThumbnailItemSelectionEvent,
    ToggleButton,
    UploadFile,
} from '@ntv360/component-pantry';
import {
    ConfirmationModalComponent,
    EmptyComponent,
    ThumbnailSkeletonComponent,
    UploaderComponent,
} from '@shared/components';
import { DealerSelectionBaseComponent } from './ui/dealer-selection-base/dealer-selection-base.component';
import { FilterByUserModalComponent } from './ui/filter-by-user-modal/filter-by-user-modal.component';

/** Pipe imports */
import { FileSizePipe } from '@shared/pipes';

/** Local imports */
import { ApiPagination, MediaLibraryService, SocketService } from '@core';
import { MediaLibraryUploadDto } from '@core/dto';
import { AuthUser } from '@core/services/auth/auth.interface';
import { AuthService } from '@core/services/auth/auth.service';
import { ApiContent, ApiContentStats } from '@core/services/media-library/media-library.interface';
import { SubNavbarService } from '@layouts/main-layout/components/sub-navbar/sub-navbar.service';
import {
    ClientTypeName,
    DeleteType,
    DropdownComponent,
    FileTypes,
    FilterOperator,
    FilterParams,
    MediaDetailsModalComponent,
    NtvTableCellDirective,
    SearchbarComponent,
    SortOrder,
    StatsCardComponent,
    StatsData,
    TableComponent,
    ViewType,
} from '@shared';
import { STAT_COLOR_CLASS } from '@shared/constants';
import {
    downloadMedia,
    getContentType,
    getFileTypes,
    getMediaType,
    getVideoDuration,
    isConversionPending,
    Logger,
    QueryParams,
    withLoading,
} from '@shared/utils';
import {
    HIDE_FOR_TM,
    MEDIA_LIBRARY_BUTTONS,
    MEDIA_LIBRARY_TABLE_COLUMNS,
    MEDIA_TYPE_OPTIONS,
} from './constants';
import { UiDealerSelection, UiThumbnailContent } from './interfaces';

/** Pipe and directive imports */
const directives = [NtvTableCellDirective];

@Component({
    selector: 'app-media-library',
    imports: [
        StatsCardComponent,
        Button,
        DropdownComponent,
        SearchbarComponent,
        ThumbnailItemComponent,
        ConfirmationModalComponent,
        TableComponent,
        MediaDetailsModalComponent,
        ...directives,
        DatePipe,
        ThumbnailSkeletonComponent,
        EmptyComponent,
        FilterByUserModalComponent,
        DealerSelectionBaseComponent,
        Modal,
        Input,
        ToggleButton,
        FormsModule,
        UploaderComponent,
    ],
    templateUrl: './media-library.component.html',
    styleUrl: './media-library.component.scss',
})
export class MediaLibraryComponent implements OnInit, AfterViewInit, OnDestroy {
    /**
     * Sub-Navbar service used to programmatically project content into its view
     */
    protected subNavbarService = inject(SubNavbarService);

    /**
     * Auth service used to resolve the current user id for uploaded media ownership.
     */
    private readonly authService = inject(AuthService);

    /**
     * Runtime environment configuration for S3 URL construction.
     */
    private readonly env = inject(EnvironmentService);

    /**
     * Current authenticated user id used as the media-library ownerId.
     */
    private readonly currentUserId = this.authService.getUserTraits<{ data: { userId: string } }>()
        ?.data.userId;

    /**
     * Inject platform id to check if the component is running in the browser
     */
    private platformId = inject(PLATFORM_ID);

    /**
     * Flag to check if the component is running in the browser
     */
    protected isBrowser = isPlatformBrowser(this.platformId);

    /**
     * Router service used to navigate to the media details page
     */
    private router = inject(Router);

    /**
     * Inject destroy ref instance for auto-unsubscribing
     */
    private destroyRef = inject(DestroyRef);

    /**
     * Inject media library service
     */
    private mediaLibraryService = inject(MediaLibraryService);

    /**
     * Inject socket service for real-time conversion updates
     */
    private socketService = inject(SocketService);

    /**
     * Inject element ref and ng zone for DOM manipulation and animations
     */
    private el = inject(ElementRef);
    private ngZone = inject(NgZone);

    /**
     * Content reference to project into the sub-navbar component
     */
    protected subNavBarContent = viewChild<TemplateRef<any>>('subNavBarContent');

    /**
     * Constant values used for html structure, contains icons, label, ang certain customization properties
     */
    protected mediaTypeOptions = MEDIA_TYPE_OPTIONS;
    protected tableColumns = MEDIA_LIBRARY_TABLE_COLUMNS;
    protected mediaLibraryButtons = MEDIA_LIBRARY_BUTTONS;
    protected showForTM = HIDE_FOR_TM;

    /**
     * Holds the media library stats
     */
    protected stats = signal<StatsData[]>([]);

    /**
     * Holds the paginated contents
     */
    protected contents = signal<UiThumbnailContent[]>([]);

    /**
     * Whether set the content to filler/filter
     */
    protected isContentFiller = signal<boolean>(false);

    /**
     * Flag whether to enable bulk-select button
     */
    protected enableBulkSelect = signal<boolean>(false);

    /**
     * The currently selected item for details
     */
    protected selectedItem = signal<ThumbnailItem | null>(null);

    /**
     * Current content view type
     */
    protected currentViewType = signal<ViewType>(ViewType.GRID);

    /**
     * A Set containing IDs of all selected items
     */
    public selectedItemIds = signal<Set<string>>(new Set());

    /**
     * Holds the number of stats card to be displayed based on the api response. Defaults to 4
     */
    protected numberOfStatsCard = signal<number>(4);

    /**
     * Debounced search key for media library contents searching
     */
    private searchKey = signal<string>('');

    /**
     * Filter parameters for media library contents
     */
    private filterParams = signal<Record<string, FilterOperator>>({});

    /**
     * Query params for media library contents
     *
     * @example
     * ```typescript
     * {
     *   sortBy: 'createdAt',
     *   sortOrder: 'asc',
     * }
     * ```
     */
    private queryParams = signal<QueryParams>({
        limit: 40,
    });

    /**
     * Whether to sort the contents in ascending or descending order. Defaults to `asc`
     */
    private sortContentsBy = signal<SortOrder>(SortOrder.ASC);

    /**
     * Pagination state for the current data set.
     * Tracks page position, limits, and navigation availability.
     * Updated after each successful API response.
     */
    protected paginationMeta = signal<ApiPagination>({
        totalCount: 0,
        page: 1,
        limit: this.queryParams().limit ?? 40,
        totalPages: 0,
        hasNextPage: false,
        hasPreviousPage: false,
    });

    /** ========================= Modal states ========================= */

    /**
     * Whether to show the media details modal
     */
    protected showMediaDetailsModal = signal<boolean>(false);

    /**
     * Whether to show the delete confirmation modal on a selected item
     */
    protected showDeleteConfirmationModal = signal<boolean>(false);

    /**
     * Whether to show the delete success modal
     */
    protected showDeleteSuccessModal = signal<boolean>(false);

    /**
     * Whether to show the filter by user modal
     */
    protected showFilterByUserModal = signal<boolean>(false);

    /**
     * Holds the filter by user signal value such as the selectedDealer,
     * selectedHost, selectedAdvertiser
     *
     * Types to an array to support the autocomplete emitted value array value.
     */
    protected filterByUser = signal<UiDealerSelection>({
        selectedAdvertiser: [],
        selectedDealer: [],
        selectedHost: [],
    });

    /**
     * Holds the uploading content for data such as the dealer, host, and selected advertiser
     * Also includes whether to optimize the video. Defaults to `true`
     *
     * Types to an array to support the autocomplete emitted value array value.
     */
    protected uploadContentFor = signal<UiDealerSelection & { optimizeVideoUpload: boolean }>({
        selectedAdvertiser: [],
        selectedDealer: [],
        selectedHost: [],
        optimizeVideoUpload: true,
    });

    /** ================== Loading states =========================  */

    /**
     * Flag whether the contents are loading
     */
    protected isLoadingContents = signal<boolean>(false);

    /**
     * Flag whether content search is loading
     */
    protected isSearchingContents = signal<boolean>(false);

    /**
     * Flag whether contents stats is loading
     */
    protected isLoadingStats = signal<boolean>(false);

    /**
     * Flag whether deleting contents is loading
     */
    protected isDeletingContents = signal<boolean>(false);

    /**
     * Flag whether loading more contents is loading
     */
    protected isLoadingMoreContents = signal<boolean>(false);

    /** ======================== End of loading states ================= */

    /**
     * Determines what kind of delete is to be executed. Either a single delete or bulk-delete
     */
    private deleteType = signal<DeleteType>(DeleteType.SINGLE);

    /** Controls the visibility of the edit content modal */
    protected showEditContentModal = signal<boolean>(false);

    /** The current value of the filename in the edit modal */
    protected editFileName = signal<string>('');

    /** Controls the visibility of  dealer selection modal */
    protected showDealerSelectionModal = signal<boolean>(false);

    /** Controls the visibility of the uploader component */
    protected showUploader = signal<boolean>(false);

    /** Controls the visibility of the single content modal */
    protected showSingleContentModal = signal<boolean>(true);

    /** Holds the duplicate selected files based on filenames. Used by the uploader component */
    protected duplicateFiles = signal<DuplicateResult[]>([]);

    /**
     * Timeout instance for the delete success modal
     */
    private deleteSuccessTimeout?: NodeJS.Timeout;

    /**
     * GetAll subscription instance to cancel previous in-flight request before starting new one when performing search or filtering
     */
    private getAllWithSearchSubscription?: Subscription;

    /**
     * Whether to enable bulk delete button if the number or selected contents reaches at least 2
     */
    protected enableBulkDelete = computed(() => this.selectedItemIds().size >= 2);

    /**
     * Search contents based on the debounced search key
     */
    constructor() {
        /**
         * Effect to load contents based on the pagination, search, and filter parameters.
         * When search key or filter params change, the page is reset to 1 so existing
         * results are replaced. When only queryParams change (e.g. next page), new
         * results are appended.
         */
        effect(() => {
            const searchKey = this.searchKey();
            const filterParams = this.filterParams();
            const queryParams = this.queryParams();

            // Load paginated contents with filtering and query parameters
            untracked(() => {
                const page = queryParams.page ?? 1;

                const trimmedSearch = searchKey.trim();

                this.getAllContents(
                    {
                        filters: {
                            ...(trimmedSearch ? { fileName: { _ilike: `${trimmedSearch}` } } : {}),
                            ...filterParams,
                        },
                        ...queryParams,
                    },
                    page > 1,
                );
            });
        });

        /**
         * Effect to re-apply conversion overlays when contents or pending conversions change.
         */
        effect(() => {
            this.contents();
            this.pendingConversions(); // Explicitly track pending conversions for zoneless update

            if (this.isBrowser) {
                this.applyConvertingOverlay();
            }
        });
    }

    /**
     * Save preference to the browser e.g the chosen layout view
     */
    ngOnInit(): void {
        if (isPlatformBrowser(this.platformId)) {
            // Ensure WebSocket connection is established and register view page action
            this.socketService.connect();
            this.socketService.sendViewPageAction('channellibrary');

            // Consolidated WebSocket Listener for Video Conversion
            this.socketService.conversionComplete$
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe((data: any) => {
                    const uuid = data.uuid || data.ContentId || data.contentId;
                    if (uuid) {
                        console.info('[MediaLibrary] WebSocket Conversion Signal:', uuid);
                        this.handleConversionComplete(uuid);
                        // Force a thumbnail refresh if possible
                        if (typeof (this as any).reloadThumbnailById === 'function') {
                            (this as any).reloadThumbnailById(uuid);
                        }
                    }
                });

            const preference = localStorage.getItem('media-library-view-preference');
            if (preference) {
                this.currentViewType.set(preference as ViewType);
            }
        }

        this.getContentsStats();
        this.setDealerIdFromCurrentUserForUploadContext();
    }

    /**
     * Checks if the current logged in user is dealer, if so, it automatically sets the
     * upload media context for that dealer.
     */
    private setDealerIdFromCurrentUserForUploadContext(): void {
        this.authService.getCurrentUser().subscribe((res) => {
            const isDealer = (res as AuthUser).data.clientTypeName === ClientTypeName.DEALER;
            const dealerId = isDealer ? (res as AuthUser).data.userId : null;
            if (isDealer && dealerId) {
                this.uploadContentFor.update((prev) => ({
                    ...prev,
                    selectedDealer: [{ label: (res as AuthUser).data.firstName, value: dealerId }],
                }));
            }
        });
    }

    /**
     * Updates the local state when a video conversion finishes
     * Removes the pending uuid from the pending list, and replaces the extension to webm format so we don't have
     * to refetch/refresh the data
     * @param uuid - The UUID of the converted video
     */
    private handleConversionComplete(uuid: string): void {
        // Find the actual item to get both the Filestack UUID and Database contentId
        const item = this.contents().find((i) => i.uuid === uuid || i.contentId === uuid);

        // Removes the pending id from the list and replace the extension with webm format
        if (item) {
            if (item.uuid) {
                this.socketService.removePendingConversion(item.uuid);
                this.replaceExtensionWithWebm(item.contentId);
            }
            if (item.contentId) {
                this.socketService.removePendingConversion(item.contentId);
                this.replaceExtensionWithWebm(item.contentId);
            }
        }

        // Optional: Trigger a slight delay and reapply the conversion overlay
        setTimeout(() => {
            this.applyConvertingOverlay();
        }, 1000);
    }

    /**
     * Project the content into the sub-navbar component after view initializes
     */
    ngAfterViewInit(): void {
        if (this.subNavBarContent()) {
            this.subNavbarService.setContent(this.subNavBarContent()!);
        }
    }

    /**
     * Clear the projected content from the sub-navbar component
     */
    ngOnDestroy(): void {
        this.subNavbarService.clearContent();
    }

    /**
     * Opens the detail view of a clicked media item in a new tab.
     *
     * @param data - The thumbnail content whose detail page will be opened
     */
    protected onItemClicked(data: UiThumbnailContent): void {
        const route = this.router.url;

        window.open(`${route}/${data.contentId}`, '_blank');
    }

    /**
     * Handles checking and unchecking of a thumbnail item checkbox.
     * Adds the item ID to the selected set when checked, removes it when unchecked.
     *
     * @param data - The selection event containing the item and its new selection state
     */
    protected onSelectionToggle(data: ThumbnailItemSelectionEvent): void {
        const currentSet = new Set(this.selectedItemIds());
        if (data.selected) {
            currentSet.add(data.item.id);
        } else {
            currentSet.delete(data.item.id);
        }
        this.selectedItemIds.set(currentSet);
    }

    /**
     * Syncs {@link selectedItemIds} with the latest selection state from the table.
     *
     * @param event - Array of currently selected thumbnail items emitted from table component
     */
    protected onSelectionChange(event: UiThumbnailContent[]): void {
        // Update the selected item ids
        this.selectedItemIds.set(new Set(event.map((item) => item.contentId)));
    }

    /**
     * Handles showing media details modal
     *
     * @param data - The data of the item to show details for
     */
    protected showMediaDetails(data: ThumbnailItem): void {
        this.selectedItem.set(data);
        this.showMediaDetailsModal.set(true);
    }

    /**
     * Handles going to the next page (improve this later)
     */
    protected onNextPage(): void {
        const paginationMeta = this.paginationMeta();

        this.queryParams.update((params) => ({
            ...params,
            page: paginationMeta.page + 1,
        }));
    }

    /** ================== Filtering ==================== */
    /**
     * Sort contents based on the sortBy value.
     *
     * @remarks
     * You might notice that the `onSortContents` seems to not exist in this file.
     * It is because the `onSortContents` is a method that is being called from the `layout.constant.ts` file.
     * The `layout.constant.ts` file is a file that contains the constants used for the layout component.
     *
     * It is defined as `'action'` property in the `layout.constant.ts` file.
     *
     * Invoked dynamically via {@link stringToFnCall} using the action name
     * defined in {@link MEDIA_LIBRARY_BUTTONS}.
     */
    protected onSortContents(): void {
        const sortContentsBy = this.sortContentsBy();
        this.sortContentsBy.set(sortContentsBy === SortOrder.ASC ? SortOrder.DESC : SortOrder.ASC);

        this.queryParams.update((params) => ({
            ...params,
            page: 1,
            sortBy: FilterParams.CREATED_AT,
            sortOrder: sortContentsBy,
        }));
    }

    /**
     * Open filter by user modal
     *
     * Invoked dynamically via {@link stringToFnCall} using the action name
     * defined in {@link MEDIA_LIBRARY_BUTTONS}.
     */
    protected onOpenFilterByUserModal(): void {
        this.showFilterByUserModal.set(true);
    }

    /**
     * Filter by selected dealer, host, and advertiser
     *
     * Invoked dynamically via {@link stringToFnCall} using the action name
     * defined in {@link MEDIA_LIBRARY_BUTTONS}.
     */
    protected onFilterByUser(filters: {
        ownerId: string | null;
        hostId: string | null;
        advertiserId: string | null;
    }): void {
        // Remove the filter params if all are null
        if (filters.ownerId === null && filters.hostId === null && filters.advertiserId === null) {
            this.filterParams.update((params) => {
                const { ownerId, hostId, advertiserId, ...rest } = params;
                return rest;
            });
            return;
        }

        // Removed ownerId or advertiserId if null
        const ownerIds = [filters.ownerId, filters.advertiserId].filter((id) => id !== null);

        const filterParams: Record<string, FilterOperator> = {
            ownerId: { _in: ownerIds },
            hostId: { _eq: filters.hostId },
        };

        // Remove null-valued objects from filterParams
        Object.keys(filters).forEach((key) => {
            if (filters[key as keyof typeof filters] === null) {
                delete filterParams[key as keyof typeof filterParams];
            }
        });
        this.filterParams.update((params) => ({
            ...params,
            ...filterParams,
        }));
        this.queryParams.update((params) => ({ ...params, page: 1 }));
    }

    /**
     * Filters content by file type.
     * Single-select: null clears the filter; a selected option applies its mapped fileType list.
     */
    protected onFilterByContentType(selected: DropdownOption | null): void {
        if (!selected) {
            this.filterParams.update((params) => {
                const { fileType, ...rest } = params;
                return rest;
            });
            this.queryParams.update((params) => ({ ...params, page: 1 }));
            return;
        }

        const fileTypes = getFileTypes((selected.value as string).toLowerCase() as FileTypes);

        this.filterParams.update((params) => ({
            ...params,
            fileType: { _in: fileTypes },
        }));
        this.queryParams.update((params) => ({ ...params, page: 1 }));
    }

    /** ======================= End of filtering ===================== */

    /**
     * Toggles the content filler filter state.
     *
     * @param checked - Whether the content filler filter is active
     */
    protected onSetContentToFilter(checked: boolean): void {
        this.isContentFiller.set(checked);
    }

    /**
     * Sets the current view layout and persists the preference to localStorage.
     *
     * @param viewType - The view layout to apply, either `'grid'` or `'list'`
     */
    protected onSetViewType(viewType: ViewType): void {
        this.currentViewType.set(viewType);

        if (isPlatformBrowser(this.platformId)) {
            localStorage.setItem('media-library-view-preference', viewType);
        }
    }

    /**
     * Forwards a table cell click to {@link onItemClicked} with the clicked row's data.
     * Opens the media details page on a new tab
     *
     * @param event - The cell click event containing the field name and row data
     */
    protected onCellClick(event: { field: string; row: UiThumbnailContent }): void {
        this.onItemClicked(event.row);
    }

    /**
     * Handles the click event for table actions via event delegation
     * @param action The action to perform
     * @param row The row data
     */
    protected onTableAction(action: string, row: UiThumbnailContent): void {
        switch (action.toLowerCase()) {
            case 'edit':
                this.selectedItem.set(row);
                this.editFileName.set(row.fileName);
                this.isContentFiller.set(!!row.isProtected);
                this.showEditContentModal.set(true);
                break;
            case 'viewdetails':
                this.showMediaDetails(row);
                break;
            case 'delete':
                this.onDeleteRequested(row);
                break;
            case 'download':
                this.onDownloadContent(row);
                break;
            case 'copy':
                this.onCopyContentUrl(row);
                break;
        }
    }

    /**
     * Handles editing content
     */
    protected onContextMenuClick(data: any): void {
        this.selectedItem.set(data);
        this.editFileName.set(data.title ?? data.name);
        this.isContentFiller.set(!!data.isProtected);
        this.showEditContentModal.set(true);
    }

    /**
     * Updates the search query used to filter media library content.
     * Resets pagination back to page 1 so results are replaced.
     *
     * @param query - The search string to filter by
     */
    protected onSearchContent(query: string): void {
        this.queryParams.update((params) => ({ ...params, page: 1 }));
        this.searchKey.set(query);
    }

    /**
     * Triggers enabling bulk-select on media contents
     *
     * Invoked dynamically via {@link stringToFnCall} using the action name
     * defined in {@link MEDIA_LIBRARY_BUTTONS}.
     */
    protected onEnableBulkSelect(): void {
        this.enableBulkSelect.update((prev) => !prev);
    }

    /**
     * Triggers the bulk delete flow for all currently selected media items.
     * Opens a confirmation modal and initiates deletion for each selected ID.
     * Sets the deleteType to be of type `DeleteType.BULK`
     *
     * Invoked dynamically via {@link stringToFnCall} using the action name
     * defined in {@link MEDIA_LIBRARY_BUTTONS}.
     */
    protected onBulkDelete(): void {
        this.showDeleteConfirmationModal.set(true);
        this.deleteType.set(DeleteType.BULK);
    }

    /**
     * Invoked when a delete action is requested.
     * Triggers the pre-delete confirmation modal before proceeding with deletion.
     *
     * @param data - The item or context to be deleted
     */
    protected onDeleteRequested(data: ThumbnailItem): void {
        this.selectedItem.set(data);
        this.showDeleteConfirmationModal.set(true);
        this.deleteType.set(DeleteType.SINGLE);
    }

    /**
     * Handles cancellation of a pending delete action.
     * Closes the confirmation modal and clears the currently selected item.
     */
    protected onCancelDelete(): void {
        this.showDeleteConfirmationModal.set(false);
        this.selectedItem.set(null);
    }

    /**
     * Confirms and executes deletion based on the current {@link deleteType}.
     *
     * - **Bulk**: Resolves target IDs from {@link selectedItemIds} and no-ops if empty.
     * - **Single**: Resolves the target ID from {@link selectedItem} and no-ops if absent.
     *
     * Delegates to {@link deleteContentsById} which handles the bulk-delete endpoint,
     * closes the confirmation modal, and clears the selection state.
     */
    protected onDeleteContent(): void {
        if (this.deleteType() === DeleteType.BULK) {
            const selectedItems = Array.from(this.selectedItemIds());
            if (selectedItems.length === 0) return;
            this.deleteContentsById(selectedItems);
            return;
        }
        const item = this.selectedItem();
        if (!item) return;

        this.deleteContentsById([item.id]);
    }

    /**
     * Invoked on successful deletion
     *
     * @remarks
     * The following happens after successful deletion:
     * - The delete confirmation modal is closed
     * - The currently selected item is cleared
     * - The success modal is shown
     * - The success modal is closed after 2 seconds
     * - The contents are reloaded
     * - The contents stats are reloaded
     * - The delete success timeout is cleared
     * - The selectedIds are cleared if there are any
     *
     * @remarks
     * If user clicks the confirm button instead of waiting the success modal to close on its own
     * clear the timeout
     */
    protected onDeleteSuccess(): void {
        if (this.deleteSuccessTimeout) {
            clearTimeout(this.deleteSuccessTimeout);
            this.deleteSuccessTimeout = undefined;
            this.showDeleteSuccessModal.set(false);
            this.showDeleteConfirmationModal.set(false);
            this.selectedItem.set(null);
            this.getAllContents(this.queryParams());
            this.getContentsStats();
            this.selectedItemIds.set(new Set<string>());
        } else {
            this.showDeleteConfirmationModal.set(false);
            this.selectedItem.set(null);
            this.showDeleteSuccessModal.set(true);
            this.deleteSuccessTimeout = setTimeout(() => {
                this.showDeleteSuccessModal.set(false);
                this.deleteSuccessTimeout = undefined;
                this.getAllContents(this.queryParams());
                this.getContentsStats();
            }, 2000);
        }
    }

    /** ==================== Uploader Utilities ===================== */

    /**
     * Handles skipping dealer selection for media upload
     * Clears selected dealer, advertiser, and host
     */
    protected onSkipDealerSelection(): void {
        // Clear selection
        this.uploadContentFor.update((prev) => {
            return {
                ...prev,
                selectedAdvertiser: [],
                selectedDealer: [],
                selectedHost: [],
            };
        });

        this.showDealerSelectionModal.set(false);
        this.showUploader.set(true);
    }

    /**
     * Handles the transition from dealer selection modal to uploader modal
     */
    protected onProceedToUpload(): void {
        this.showDealerSelectionModal.set(false);
        this.showUploader.set(true);
    }

    /**
     * Downloads the media file associated with a thumbnail item.
     * No-ops if the item has no URL.
     *
     * @param data - The thumbnail item whose media file will be downloaded
     */
    protected async onDownloadContent(data: any): Promise<void> {
        const url = data.url || data.src;
        if (url) {
            await downloadMedia(url, data.name || 'download');
        } else {
            console.warn('Cannot download media: No URL found in item data', data);
        }
    }

    /**
     * Copies the media content url to the clipboard
     * No-ops if the item has no URL.
     *
     *  @param data - The thumbnail item whose media url will be copied
     */
    protected async onCopyContentUrl(data: any): Promise<void> {
        const url = data.url || data.src;
        if (url) {
            if (navigator.clipboard) {
                await navigator.clipboard.writeText(url);
            } else {
                const textArea = document.createElement('textarea');
                textArea.value = url;
                textArea.style.position = 'fixed';
                textArea.style.left = '-999999px';
                document.body.appendChild(textArea);
                textArea.focus();
                textArea.select();
                try {
                    document.execCommand('copy', true);
                } catch (err) {
                    console.error('Failed to copy URL:', err);
                }
                document.body.removeChild(textArea);
            }
        } else {
            console.warn('Cannot copy URL: No URL found in item data', data);
        }
    }

    /** ========================= Utilities ============================= */

    /** Get equivalent background color of a tag based on content type
     * @param contentType The content type
     * @returns {string} The background color
     */
    protected getTagColor(contentType: string): string {
        const baseClass = 'rounded-full w-fit px-2.5 py-0.5 text-[10px] font-medium';
        switch (contentType) {
            case 'image':
                return `${baseClass} bg-information-50 text-information-main`;
            case 'video':
                return `${baseClass} bg-danger-50 text-danger-main`;
            default:
                return `${baseClass} bg-information-50 text-information-main`;
        }
    }

    /**
     * Get the color class value for the stats card trend
     * @returns {string} The colorClass value based on the trend status
     */
    public getTrendColorClass(trend: string): string {
        if (trend === 'up') return STAT_COLOR_CLASS.up;
        if (trend === 'down') return STAT_COLOR_CLASS.down;
        return '';
    }

    /**
     * Convert string name into a function and invoke it
     * @param name The name of the function
     * @returns {Function} The function
     */
    protected stringToFnCall(name: string): void {
        const fn = (this as any)[name];
        if (typeof fn === 'function') {
            fn.call(this);
        }
    }

    /**
     * Determines the effective button variant based on its label and current UI state.
     *
     * Overrides the provided `variant` for specific bulk actions when their corresponding
     * features are enabled:
     * - "Bulk Select" → returns `'accent'` if bulk select is enabled
     * - "Bulk Delete" → returns `'danger'` if bulk delete is enabled
     *
     * Falls back to the original `variant` if no conditions are met.
     *
     * @param variant - The default variant assigned to the button.
     * @param label - The display label of the button used to determine conditional overrides.
     *
     * @returns The resolved button variant after applying conditional logic.
     */
    protected resolveButtonVariant(variant: string, label: string): string {
        if (label === 'Bulk Select') {
            return this.enableBulkSelect() ? 'accent' : variant;
        }
        if (label === 'Bulk Delete') {
            return this.enableBulkDelete() ? 'danger' : variant;
        }
        return variant;
    }

    /**
     * Resolves the button text based on the current UI state.
     *
     * @param label - The display label of the button used to determine conditional overrides.
     * @param id - An optional identifier used to identify which button is being currently checked
     *
     * @returns The text to be displayed on the button
     */
    protected resolveButtonText(label: string, id?: string): string {
        if (id === 'sort-button') {
            return this.sortContentsBy() === SortOrder.ASC ? 'Ascending' : 'Descending';
        }
        return label;
    }

    /** ======================== End of utility methods ======================== */

    /**
     * Determines whether bulk select should be disabled for a given control value.
     *
     * - `undefined` — not disabled
     * - `boolean` — used directly
     * - `string` — resolves against the `enableBulkDelete` signal when value is equal to `'isBulkDeleteEnabled'`
     *
     * @param value - The control value to evaluate
     * @returns `true` if bulk select should be disabled, `false` otherwise
     *
     * @see {@link MEDIA_LIBRARY_BUTTONS } Check the disabled property of the button found in `"rightControls"` array
     */
    protected isBulkSelectDisabled(value: string | boolean | undefined): boolean {
        if (value === undefined) return false;
        if (typeof value === 'boolean') return value;
        return value === 'isBulkDeleteEnabled' ? !this.enableBulkDelete() : false;
    }

    /**
     * Maps API response to UI requirements
     *
     * @param stats - Media library overall stats
     * @param contents - Paginated contents
     *
     * @remarks
     * Parameters are marked as nullable since some method calls only pass one of the two parameters.
     *
     * @param append - When `true`, new contents are appended to existing ones (pagination).
     *                 When `false`, contents are replaced (search/filter reset).
     */
    private mapDataToUi(
        stats: ApiContentStats | null,
        contents: ApiContent[] | null,
        append = false,
    ): void {
        if (stats) {
            // Map stats
            const uiStats: StatsData[] = [
                this.createStatItem(
                    'Contents',
                    stats.totalContents,
                    'folder',
                    '#3ADB30',
                    '#E3FAE1',
                    'All uploaded media across categories.',
                ),
                this.createStatItem(
                    'Videos',
                    stats.totalVideos,
                    'videos',
                    '#E73535',
                    '#FDF1F1',
                    'Includes .mp4, .mov, and other video formats.',
                ),
                this.createStatItem(
                    'Images',
                    stats.totalImages,
                    'images',
                    '#095AF3',
                    '#EEF4FE',
                    'Image assets used for ad displays.',
                ),
            ];

            this.stats.set(uiStats);
            this.numberOfStatsCard.set(uiStats.length);
        }

        if (contents) {
            // Map media library contents to the UI requirements
            const mapped = contents.map((item) => {
                const { fileType, ...rest } = item;
                return {
                    ...rest,
                    id: item.contentId,
                    name: item.title ?? item.fileName,
                    ownerName: item.ownerName,
                    type: getContentType(item.fileType),
                    src: item.url,
                    thumbnail: item.thumbnail,
                    isConverted: !!item.isConverted,
                    size: new FileSizePipe().transform(item.fileSize),
                    duration: item.duration ? Number(item.duration) : 0,
                    modified: item.createdAt ? new Date(item.createdAt) : undefined,
                    createdAt: item.createdAt,
                    uuid: item.uuid || (item.metadata as any)?.uuid,
                } as UiThumbnailContent;
            });

            // Append to existing contents or replace depending on the flag
            if (append) {
                this.contents.update((prev) => [...prev, ...mapped]);
            } else {
                this.contents.set(mapped);
            }
        }
    }

    /**
     * Replaces the extension of the video file once it is converted to webm and set the isConverted: true
     * This prevents unneccessary network calls just to update the current page's data,
     * This also adds a param to the url to force the component to reload the data
     *
     * Invokes the reloading of the thumbnail by id
     *
     * @param contentId - The contentId of the converted video
     */
    private replaceExtensionWithWebm(contentId: string): void {
        const timestamp = Date.now();
        this.contents.update((prev) =>
            prev.map((item) => {
                if (item.contentId === contentId) {
                    const newUrl = item.url.replace(/\.[^/.]+$/, '.webm');

                    let thumbnail = item.thumbnail;
                    if (!thumbnail && (item.type === 'video' || item.type === 'filler')) {
                        const cleanUrl = item.url.split('?')[0];
                        thumbnail = cleanUrl.replace(/\.[^/.]+$/, '.jpg');
                    }
                    const thumbnailSeparator = thumbnail?.includes('?') ? '&' : '?';

                    return {
                        ...item,
                        isConverted: true,
                        url: newUrl,
                        thumbnail: thumbnail
                            ? `${thumbnail}${thumbnailSeparator}_reload=${timestamp}`
                            : undefined,
                    };
                }
                return item;
            }),
        );

        requestAnimationFrame(() => {
            this.reloadThumbnailById(contentId);
        });
    }

    /**
     * Helper to create stat items for the UI
     */
    private createStatItem(
        title: string,
        stat: any,
        iconKey: string,
        iconColor: string,
        bgColor: string,
        description: string,
    ): StatsData {
        const thisWeekTrend = stat.thisWeek >= stat.lastWeek ? 'up' : 'down';
        const lastWeekTrend = stat.lastWeek >= stat.twoWeeksAgo ? 'up' : 'down';

        return {
            title,
            description,
            iconKey,
            customColor: { iconColor, bgColor },
            total: stat.overall.toString(),
            stats: [
                {
                    label: 'Last Week',
                    value: stat.lastWeek.toString(),
                    colorClass: 'orange',
                    variant: 'trend',
                    trend: {
                        value: lastWeekTrend,
                        colorClass: this.getTrendColorClass(lastWeekTrend),
                    },
                },
                {
                    label: 'This Week',
                    value: stat.thisWeek.toString(),
                    colorClass: 'orange',
                    variant: 'trend',
                    trend: {
                        value: thisWeekTrend,
                        colorClass: this.getTrendColorClass(thisWeekTrend),
                    },
                },
            ],
        };
    }

    /** ========================= API Calls ============================= */

    /**
     * Load medial library contents and contents stats
     *
     * @param queryParams - The query parameters to apply to the contents
     * @param append - Whether to append new results to existing contents (for pagination)
     *
     * @example
     * ```typescript
     * queryParams = {sortBy: "createdAt", sortOrder: "desc"}
     * ```
     */
    private getAllContents(queryParams: QueryParams, append = false): void {
        // Cancel previous in-flight request before we trigger a new one
        this.getAllWithSearchSubscription?.unsubscribe();

        // Flag loading more contents if append is true
        if (append) {
            this.isLoadingMoreContents.set(true);
        }

        // Trigger search and store the subscription
        this.getAllWithSearchSubscription = forkJoin({
            contents: this.mediaLibraryService.getContents(queryParams),
        })
            .pipe(
                withLoading(this.isLoadingContents),
                catchError((error: unknown) => {
                    Logger.error('MediaLibrary', 'error fetching contents', error);
                    return EMPTY;
                }),
            )
            .subscribe(({ contents }) => {
                // Update pagination meta state
                this.paginationMeta.set(contents.meta.pagination);

                // Map data to UI (append for pagination, replace for search/filter)
                this.mapDataToUi(null, contents.data, append);

                // Unflag loading more contents
                this.isLoadingMoreContents.set(false);
            });
    }

    /**
     * Load media library contents stats
     */
    private getContentsStats(): void {
        this.mediaLibraryService
            .getContentsStats()
            .pipe(
                withLoading(this.isLoadingStats),
                catchError((error: unknown) => {
                    Logger.error('MediaLibrary', 'error fetching contents stats', error);
                    return EMPTY;
                }),
            )
            .subscribe((stats) => {
                this.mapDataToUi(stats.data, null);
            });
    }

    /** Track currently converting videos from socket service */
    protected pendingConversions = toSignal(this.socketService.pendingConversions$, {
        initialValue: new Set<string>(),
    });

    /**
     * Finalizes the upload process by persisting Filestack metadata to the database.
     * * Lifecycle:
     * 1. Maps the Filestack response and component state to a MediaLibraryUploadDto.
     * 2. Sends metadata to the backend via `mediaLibraryService`.
     * 3. On success, refreshes the library view and statistics.
     * 4. Executes the provided callback to transition the UI to the 'uploaded' state.
     *
     * @param params - Configuration object containing:
     * - `file`: The extended file object including the verified Filestack URL.
     * - `cb`: The completion callback (`markFileUploaded`) to notify the ntv-uploader component.
     */
    protected async onUploadFile({
        file,
        cb,
    }: {
        file: UploadFile & { url: string; s3Url?: string; uuid?: string; optimizeVideo?: boolean };
        cb: (fileId: string, url: string) => void;
    }): Promise<void> {
        const isVideo = getMediaType(file) === 'video';

        // Hardened: Extract handle if S3 URL is missing to reconstruct it
        let contentUrl = file.s3Url || (file as any).s3url;
        if (!contentUrl && file.url) {
            const handle = (file as any).handle || file.url.split('/').pop()?.split('?')[0];
            // Manually reconstruct S3 URL as a fallback
            contentUrl = `https://${this.env.config.s3.bucketName}.s3.${this.env.config.s3.region}.amazonaws.com/${handle}`;
        }

        // Final fallback
        contentUrl = contentUrl || file.url;

        // To check if the content is convertion is underway is through checking the presence of metadata and isConverted: false and
        // optimizeVideo is set to true,
        // if all are present, then it should by considered as converting
        // Since we need thumbnail generation, we still need to attach the metadata even if optimization is not enabled
        // So basically the metadata is just there for thumbnail generation purposes.
        const optimizedContentMetadata =
            file.optimizeVideo || isVideo
                ? {
                      metadata: JSON.stringify({
                          uuid: file.uuid,
                          optimizeVideo: file.optimizeVideo,
                      }),
                  }
                : {};

        // Generate final URLs - swap extensions for videos
        let thumbnailUrl = contentUrl;
        if (isVideo && contentUrl.includes('amazonaws.com')) {
            const baseS3Url = contentUrl.replace(/\.[^/.]+$/, '');
            thumbnailUrl = baseS3Url + '.jpg';
        }

        const mediaLibraryContent: MediaLibraryUploadDto = {
            title: file.currentName,
            ownerId: this.currentUserId ?? '',
            fileName: file.currentName.toLowerCase().endsWith(file.extension.toLowerCase())
                ? file.currentName
                : `${file.currentName}.${file.extension}`,
            fileSize: file.size,
            fileType: file.mimeType,
            url: contentUrl,
            duration: !isVideo ? 0 : Math.floor(await getVideoDuration(file.file)),
            thumbnail: thumbnailUrl,
            isConverted: !isVideo || !file.optimizeVideo,
            isProtected: false,
            uuid: file.uuid,
            ...optimizedContentMetadata,
            isChannel: false,
            contentAssignments: {
                hostLocationId: this.uploadContentFor().selectedHost?.[0]?.value ?? null,
                dealerId: this.uploadContentFor().selectedDealer?.[0]?.value ?? null,
                advertiserId: this.uploadContentFor().selectedAdvertiser?.[0]?.value ?? null,
            },
        };

        Logger.info('MediaLibrary', 'this is the upload is happening', mediaLibraryContent);

        this.mediaLibraryService
            .uploadContent(mediaLibraryContent)
            .pipe(
                catchError((error: unknown) => {
                    Logger.error('MediaLibrary', 'error uploading content', error);
                    return EMPTY;
                }),
            )
            .subscribe((res: any) => {
                // Track conversion via socket only when video optimization is enabled
                if (isVideo && file.optimizeVideo) {
                    if (file.uuid) {
                        console.info(
                            '[MediaLibrary] Upload success, tracking conversion for UUID:',
                            file.uuid,
                        );
                        this.socketService.sendVideoConversionUuid(file.uuid);
                    } else {
                        console.warn(
                            '[MediaLibrary] Upload success for video, but NO UUID found for tracking',
                        );
                    }
                }

                this.getContentsStats();
                this.getAllContents(this.queryParams());

                // Invoke the callback function to make the uploader component know that the file has been saved to the database
                // Using the markFileUpladed function
                cb(file.id, file.s3Url ?? file.url);
            });
    }

    /**
     * Handles saving the edited content
     */
    protected onSaveEdit(): void {
        const item = this.selectedItem();
        if (!item) return;

        const updatedData = {
            fileName: this.editFileName(),
            isProtected: this.isContentFiller(), // Assuming isProtected or a similar field mapping
        };

        this.mediaLibraryService
            .updateContent(item.id, updatedData)
            .pipe(
                withLoading(this.isLoadingContents), // Or use a separate loading state
                catchError((error: unknown) => {
                    Logger.error('MediaLibrary', 'error updating content', error);
                    return EMPTY;
                }),
            )
            .subscribe(() => {
                this.showEditContentModal.set(false);
                // Reload contents to reflect changes
                this.getAllContents(this.queryParams());
                this.getContentsStats();
            });
    }

    /**
     * Delete contents by their id or ids. Supports single and bulk-delete
     *
     * @param contentIds - The IDs of the contents to delete
     *
     * @remarks
     * This method is used to delete a single content by its id or multiple contents by their ids.
     * After deletion, it will reload the contents and contents stats
     */
    private deleteContentsById(contentIds: string[]): void {
        this.mediaLibraryService
            .deleteContents(contentIds)
            .pipe(
                withLoading(this.isDeletingContents),
                catchError((error: unknown) => {
                    Logger.error('MediaLibrary', 'error deleting contents', error);
                    return EMPTY;
                }),
            )
            .subscribe(() => {
                this.onDeleteSuccess();
            });
    }

    /**
     * @public
     * @description Applies "Converting..." overlay to items that are still being converted.
     * Uses data-content-id for reliable mapping between DOM elements and data items.
     */
    public applyConvertingOverlay(): void {
        if (!this.isBrowser) return;

        requestAnimationFrame(() => {
            const hostEl = this.el.nativeElement as HTMLElement;
            // Select all ntv-thumbnail-item elements
            const elements = Array.from(
                hostEl.querySelectorAll('ntv-thumbnail-item'),
            ) as HTMLElement[];

            elements.forEach((el) => {
                const contentId = el.getAttribute('data-content-id');
                if (!contentId) return;

                const dataItem = this.contents().find((item) => item.contentId === contentId);
                if (!dataItem) return;

                // Ensure only videos get the overlay
                const isVideo = dataItem.type === 'video';
                if (!isVideo) {
                    el.classList.remove('conversion-pending');
                    this.removeConvertingOverlay(el);
                    return;
                }

                /**
                 * Logic for "Converting..." overlay:
                 * 1. If the item is in the socket's pending set (actively being tracked by UUID).
                 * 2. If the database flag isConverted is explicitly false.
                 * 3. If the content has metadata  in it, metadata is applied to video contents
                 *    It contains a "videoOptimize" flag that needs to be true for conversion to be considered as underway
                 *
                 * The present of metadata and isConverted: false, should be enough to indicate that conversion is underway
                 */
                const isOptimizeVideoEnabled =
                    dataItem.metadata !== null && dataItem.metadata['optimizeVideo'] === true;

                const isPendingInSocket =
                    isOptimizeVideoEnabled && dataItem.uuid
                        ? this.pendingConversions().has(dataItem.uuid)
                        : false;

                if (
                    isConversionPending(
                        dataItem.isConverted,
                        isOptimizeVideoEnabled,
                        dataItem?.fileName || '',
                        isPendingInSocket,
                    )
                ) {
                    el.classList.add('conversion-pending');
                    this.injectConvertingOverlay(el);
                } else {
                    el.classList.remove('conversion-pending');
                    this.removeConvertingOverlay(el);
                }
            });
        });
    }

    /**
     * Markup for the injected "Converting" overlay. Uses the spinning NTV compass
     * mark (`ncompass-cursor`) as the loader and an indeterminate progress bar, since
     * the media library has no per-tile Filestack progress source.
     */
    private readonly convertingOverlayHtml = `
        <div class="conversion-status-overlay conversion-status-overlay--indeterminate" data-conversion-overlay role="status" aria-live="polite" aria-label="Converting video">
            <div class="conversion-status-overlay__logo-bg" aria-hidden="true">
                <svg class="conversion-status-overlay__logo" width="28" height="28" viewBox="0 0 2016 2000" aria-hidden="true">
                    <use href="assets/icons/icons-sprite.svg#ncompass-cursor"></use>
                </svg>
            </div>
            <div class="conversion-status-overlay__content">
                <span class="conversion-status-overlay__caption">Converting</span>
            </div>
            <div class="conversion-status-overlay__bar" aria-hidden="true">
                <div class="conversion-status-overlay__bar-fill"></div>
            </div>
        </div>`;

    /**
     * Injects the converting overlay into a thumbnail element if not already present.
     *
     * @param el - The `ntv-thumbnail-item` host element.
     */
    private injectConvertingOverlay(el: HTMLElement): void {
        if (el.querySelector('[data-conversion-overlay]')) return;
        el.insertAdjacentHTML('beforeend', this.convertingOverlayHtml);
    }

    /**
     * Removes the converting overlay from a thumbnail element if present.
     *
     * @param el - The `ntv-thumbnail-item` host element.
     */
    private removeConvertingOverlay(el: HTMLElement): void {
        el.querySelector('[data-conversion-overlay]')?.remove();
    }

    /**
     * @public
     * @description Reloads a specific thumbnail by contentId when conversion finishes.
     * Adds a cache buster to img/video tags to force reload from CDN.
     */
    public reloadThumbnailById(contentId: string): void {
        if (!this.isBrowser) return;

        this.ngZone.runOutsideAngular(() => {
            let attempts = 0;
            const maxAttempts = 10;

            const checkAndUpdate = () => {
                const hostEl = this.el.nativeElement as HTMLElement;
                // Find the specific element by its data-content-id attribute
                const el = hostEl.querySelector(
                    `ntv-thumbnail-item[data-content-id="${contentId}"]`,
                ) as HTMLElement;

                if (!el) {
                    attempts++;
                    if (attempts < maxAttempts) {
                        setTimeout(checkAndUpdate, 100);
                    }
                    return;
                }

                el.classList.remove('conversion-pending');
                this.removeConvertingOverlay(el);

                // Update img element with cache buster
                const img = el.querySelector('img') as HTMLImageElement;
                if (img) {
                    // Clear manual display style hidden by pantry's internal error handler
                    img.style.display = '';
                    img.style.removeProperty('display');
                    img.classList.remove('thumbnail-item__image--error');
                    img.classList.remove('thumbnail-item__video-thumbnail--error');

                    if (img.src) {
                        // Always force reload the source with a new cache buster timestamp
                        const cleanSrc = img.src.split('_reload=')[0].replace(/[&?]$/, '');
                        const separator = cleanSrc.includes('?') ? '&' : '?';
                        img.src = `${cleanSrc}${separator}_reload=${Date.now()}`;
                    }
                } else {
                    // If image is not in DOM yet (e.g. pending Angular change detection), retry
                    attempts++;
                    if (attempts < maxAttempts) {
                        setTimeout(checkAndUpdate, 100);
                    }
                }

                // Update video poster with cache buster
                const video = el.querySelector('video') as HTMLVideoElement;
                if (video && video.poster) {
                    const separator = video.poster.includes('?') ? '&' : '?';
                    video.poster = `${video.poster}${separator}_reload=${Date.now()}`;
                }
            };

            setTimeout(checkAndUpdate, 50);
        });
    }
}
